import qs from 'qs';
import { ITEMS_PER_PAGE } from '@shared/hooks/useFetchList';
import { isEmptyFilterValue, isSplitFilterValue, toSplitFilterValue } from './filterValue';

// 'MM/DD/YYYY' (or the range 'MM/DD/YYYY,MM/DD/YYYY' the DateFilter
// emits) -> ISO 'YYYY-MM-DD[,YYYY-MM-DD]'. Non-date-looking parts
// pass through untouched.
const mdyToIsoQValue = value =>
  String(value)
    .split(',')
    .map(part => {
      const [m, d, y] = part.split('/');
      return m && d && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : part;
    })
    .join(',');

export const getEndpoint = (baseEndpoint, baseParams, sorting = [], newFilter = []) => {
  // Reserved baseParams key (never serialized into the URL): lists
  // filter ids whose values the backend expects as ISO dates
  // (`q=dos:2026-07-01,2026-07-31`) instead of the dashboard-standard
  // MM/DD/YYYY the date pickers emit. Declared per-endpoint by the
  // owning hook (first consumer: Claims Manager cpt-claims).
  const { isoDateQFields, ...restBaseParams } = baseParams || {};

  const encodeList = values =>
    values.map(item => window.btoa(unescape(encodeURIComponent(item)))).join(',');

  const filters = (newFilter || [])
    .filter(({ value }) => {
      if (isSplitFilterValue(value)) {
        return !isEmptyFilterValue(value);
      }
      if (Array.isArray(value)) {
        return value.length > 0 && value.join('').replace(' ', '').length > 0;
      } else {
        return !!value;
      }
    })
    // flatMap, not map: a field that both includes and excludes emits two
    // terms. They must be two separate `q` parameters — the API reads one
    // positive and one negative term per field, and answers 422 to two of
    // the same sign.
    .flatMap(({ id, value }) => {
      // `!` goes after the colon and prefixes the whole (comma-joined)
      // value list, per the API's contract: `q=createdBy:!<b64>,<b64>`
      // means NOT (a OR b), not "NOT a, or b".
      if (isSplitFilterValue(value)) {
        const { include, exclude } = toSplitFilterValue(value);
        const terms = [];
        if (include.length) terms.push(`${id}:${encodeList(include)}`);
        if (exclude.length) terms.push(`${id}:!${encodeList(exclude)}`);
        return terms;
      }

      let resultFilter = '';
      if (Array.isArray(value)) {
        resultFilter = encodeList(value);
      } else if (isoDateQFields?.includes(id)) {
        resultFilter += mdyToIsoQValue(value);
      } else {
        resultFilter += value;
      }
      return [`${id}:${resultFilter}`];
    });

  // Callers may contribute their own q entries alongside the table's
  // column filters — a single `field:value` string or an array of them
  // (e.g. the BHI exception preset + its threshold companion). Values
  // are sent raw, matching the plain single-value branch above.
  if (baseParams.q) {
    filters.push(...[].concat(baseParams.q));
  }

  let sort;
  if (sorting && sorting.length) {
    const { id, desc } = sorting[0];

    const direction = desc ? '-' : '';
    sort = `${direction}${id}`;
  }
  const qsParams = {
    ...baseParams,
    q: filters,
    sort,
    pageSize: ITEMS_PER_PAGE,
  };
  return `${baseEndpoint}${qs.stringify(qsParams, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  })}`;
};

export const getTokenEndpoint = (baseEndpoint, token) =>
  `${baseEndpoint}${qs.stringify(
    {
      'continuation-token': token,
    },
    { addQueryPrefix: true }
  )}`;
